// SignaturePad — shared drawn-signature canvas (touch + mouse).
// Used for staff enrollment now; doubles as the in-room client pad later.
import React, { useRef, useState, useEffect, useCallback } from 'react';

export default function SignaturePad({ onChange, height = 160 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [empty, setEmpty] = useState(true);

  const getPos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };

  const start = useCallback((e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }, []);

  const move = useCallback((e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = getPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk.current) { hasInk.current = true; setEmpty(false); }
  }, []);

  const end = useCallback((e) => {
    if (!drawing.current) return;
    if (e) e.preventDefault();
    drawing.current = false;
    if (hasInk.current && onChange) onChange(canvasRef.current.toDataURL('image/png'));
  }, [onChange]);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, c.width, c.height);
    setupStroke(ctx);
    hasInk.current = false;
    setEmpty(true);
    if (onChange) onChange(null);
  };

  const setupStroke = (ctx) => {
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  // Canvas init runs ONCE - setting c.width erases all ink, so it must never
  // re-run when parent re-renders with a fresh onChange identity.
  useEffect(() => {
    const c = canvasRef.current;
    c.width = 600;
    c.height = 200;
    const ctx = c.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, c.width, c.height);
    setupStroke(ctx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mouse release anywhere in the window finalizes the stroke (covers releases
  // that happen after the cursor drifted off the canvas).
  useEffect(() => {
    const up = (e) => { if (drawing.current) end(e); };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [end]);

  // Touch listeners attach separately (re-attaching never touches the bitmap).
  // Non-passive so preventDefault stops page scroll while signing.
  useEffect(() => {
    const c = canvasRef.current;
    c.addEventListener('touchstart', start, { passive: false });
    c.addEventListener('touchmove', move, { passive: false });
    c.addEventListener('touchend', end, { passive: false });
    return () => {
      c.removeEventListener('touchstart', start);
      c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', end);
    };
  }, [start, move, end]);

  return (
    <div>
      <canvas ref={canvasRef}
        onMouseDown={start} onMouseMove={move} onMouseUp={end}
        onMouseLeave={() => { drawing.current = false; }}
        onMouseEnter={(e) => { if (e.buttons === 1) start(e); }}
        style={{ width: '100%', height, border: '2px dashed #94a3b8', borderRadius: 10, background: 'white', touchAction: 'none', cursor: 'crosshair', display: 'block' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{empty ? 'Sign above with finger, stylus, or mouse' : 'Looks good? You can clear and retry.'}</span>
        <button type="button" onClick={clear} style={{ padding: '4px 12px', fontSize: 12, background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, color: '#64748b', cursor: 'pointer' }}>Clear</button>
      </div>
    </div>
  );
}
